import React, { useState, useRef, useEffect } from 'react';
import Select from '../shared/Select';
import SelectArrow from '../shared/SelectArrow';
import { FormattedMessage } from 'react-intl';
import MediaQuery from 'react-responsive';
import { isFirefox } from 'react-device-detect';

export default function PassengerSelect(props) {
  const { current = {}, currentSeat = {}, locale, length } = props;
  const { index = 0 } = current;
  const { seat = '' } = currentSeat;
  const PASSENGER_NAME_CONDENSE_BARRIER = 880;

  const formatter = new Intl.NumberFormat(locale);
  const outOfText = `${formatter.format(index + 1)} / ${formatter.format(
    length
  )}`;

  // New design: custom dropdown
  if (props.useNewDesign) {
    return <GenericPassengerSelectNew {...props} outOfText={outOfText} />;
  }

  // Legacy behavior: native select with responsive behavior
  return (
    <MediaQuery
      minWidth={PASSENGER_NAME_CONDENSE_BARRIER / props.relativeWidthRatio}
    >
      {(matches) => {
        return matches ? (
          <Select
            {...props}
            selectingPassenger={true}
            ariaLabel="Select passenger"
          >
            {props.children}
          </Select>
        ) : (
          GenericPassengerSelect(props, seat, outOfText)
        );
      }}
    </MediaQuery>
  );
}

function GenericPassengerSelect(props, seat, outOfText) {
  const selectId = `${props.id || 'passenger'}-select`;
  const labelId = `${selectId}-label`;
  const ariaLabel = 'Select passenger';

  return (
    <div
      className="gordian-select gr-flex-col gr-text-title gr-bg-white gr-w-3/6 gr-h-full gr-relative gr-border gr-text-sm"
      id={props.id}
    >
      <label id={labelId} htmlFor={selectId} className="gr-sr-only">
        {ariaLabel}
      </label>
      <select
        onChange={(event) => props.handleChange(event.target.value)}
        className="gr-block gr-appearance-none gr-w-full gr-h-full gr-bg-white gr-font-normal gr-rounded-none gr-pl-6 gr-pb-4"
        value={props.value}
        id={selectId}
        aria-label={ariaLabel}
        aria-labelledby={labelId}
        title={ariaLabel}
      >
        {props.children}
      </select>
      <div
        className={`gr-pointer-events-none gr-text-secondary-text gr-text-sm gr-absolute gr-inset-y-0 gr-pl-6 gr-py-1 gr-mt-10 ${
          isFirefox ? 'gr-ml-1' : ''
        }`}
      >
        {seat ? (
          seat
        ) : props.value !== null ? (
          <FormattedMessage id="selecting" defaultMessage="Selecting..." />
        ) : (
          ''
        )}
      </div>
      <div className="gr-pointer-events-none gr-absolute gr-inset-y-0 gr-right-0 gr-flex gr-items-center gr-px-2">
        <div className="gr-hidden sm:gr-block gr-text-xs  gr-text-secondary-text gr-mr-1 sm:gr-mr-2">
          {outOfText}
        </div>
        <SelectArrow />
      </div>
    </div>
  );
}

function GenericPassengerSelectNew({
  allPassengers,
  allSeats,
  currentSeatmap,
  handleChange,
  id,
  current,
  value,
  outOfText,
  children,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  if (allPassengers && allPassengers.length > 0) {
    const activePax =
      allPassengers.find((p) => p.passenger_id === current?.passenger_id) ||
      allPassengers[0];
    const activeInitials =
      `${activePax?.first_names?.[0] ?? ''}${activePax?.surname?.[0] ?? ''}`.toUpperCase();
    const activeSeat = allSeats?.find(
      (s) =>
        s.passenger_id === activePax?.passenger_id &&
        s.segment_id === currentSeatmap
    );

    return (
      <div className="gr-relative gr-flex-shrink-0" ref={containerRef} id={id}>
        {/* Trigger — same structure as flight trigger */}
        <button
          onClick={() => setOpen((o) => !o)}
          className="gr-flex gr-items-center gr-rounded-lg gr-border gr-bg-white gr-transition-all gr-duration-150 gr-cursor-pointer hover:gr-shadow-md hover:gr-border-gray-400"
          style={{
            gap: '10px',
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '12px',
            paddingRight: '12px',
            borderColor: open
              ? 'var(--gordian-accent-color, #111827)'
              : '#E5E7EB',
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Select passenger"
        >
          {/* Avatar */}
          <div
            className="gr-relative gr-flex-shrink-0 gr-rounded-full gr-flex gr-items-center gr-justify-center gr-text-sm gr-font-bold"
            style={{
              width: '34px',
              height: '34px',
              backgroundColor: activeSeat
                ? 'var(--gordian-accent-color, #111827)'
                : '#F3F4F6',
              color: activeSeat
                ? 'var(--gordian-accent-contrast-color, #ffffff)'
                : '#6B7280',
            }}
          >
            {activeInitials}
            {activeSeat && (
              <span
                className="gr-absolute gr-rounded-full"
                style={{
                  backgroundColor: '#22C55E',
                  width: '9px',
                  height: '9px',
                  top: '-1px',
                  right: '-1px',
                  border: '2px solid #ffffff',
                }}
              />
            )}
          </div>
          {/* Name + seat */}
          <div
            className="gr-flex gr-flex-col gr-items-start"
            style={{ gap: '1px' }}
          >
            <span
              className="gr-text-sm gr-font-medium gr-whitespace-nowrap gr-text-gray-900"
              style={{
                maxWidth: '100px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {activePax?.first_names || activeInitials}
            </span>
            <span
              className="gr-text-xs gr-whitespace-nowrap"
              style={{
                color: activeSeat
                  ? 'var(--gordian-accent-color, #111827)'
                  : '#9CA3AF',
                fontWeight: activeSeat ? 600 : 400,
              }}
            >
              {activeSeat ? activeSeat.seat : 'No seat'}
            </span>
          </div>
          <svg
            width="13"
            height="13"
            viewBox="0 0 13 13"
            fill="none"
            style={{
              flexShrink: 0,
              transition: 'transform 0.15s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              color: '#9CA3AF',
            }}
          >
            <path
              d="M2 4.5l4.5 4.5 4.5-4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Dropdown */}
        {open && (
          <div
            className="gr-absolute gr-top-full gr-right-0 gr-mt-1 gr-bg-white gr-rounded-xl gr-border gr-border-gray-200 gr-shadow-lg gr-z-50 gr-overflow-hidden"
            style={{ minWidth: '100%' }}
            role="listbox"
            aria-label="Select passenger"
          >
            {allPassengers.map((pax, idx) => {
              const isActive = pax.passenger_id === current?.passenger_id;
              const initials =
                `${pax.first_names?.[0] ?? ''}${pax.surname?.[0] ?? ''}`.toUpperCase();
              const paxSeat = allSeats?.find(
                (s) =>
                  s.passenger_id === pax.passenger_id &&
                  s.segment_id === currentSeatmap
              );

              return (
                <button
                  key={pax.passenger_id}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    handleChange(idx);
                    setOpen(false);
                  }}
                  className={`gr-w-full gr-flex gr-items-center gr-gap-3 gr-text-left gr-transition-colors gr-duration-100 gr-cursor-pointer ${isActive ? '' : 'hover:gr-bg-gray-50'}`}
                  style={{
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    paddingLeft: '14px',
                    paddingRight: '14px',
                    backgroundColor: isActive
                      ? 'var(--gordian-accent-color, #111827)'
                      : undefined,
                  }}
                >
                  <div
                    className="gr-relative gr-flex-shrink-0 gr-rounded-full gr-flex gr-items-center gr-justify-center gr-text-sm gr-font-bold"
                    style={{
                      width: '34px',
                      height: '34px',
                      backgroundColor: isActive
                        ? 'rgba(255,255,255,0.15)'
                        : paxSeat
                          ? 'var(--gordian-accent-color, #111827)'
                          : '#F3F4F6',
                      color: isActive
                        ? 'var(--gordian-accent-contrast-color, #ffffff)'
                        : paxSeat
                          ? 'var(--gordian-accent-contrast-color, #ffffff)'
                          : '#6B7280',
                    }}
                  >
                    {initials}
                    {paxSeat && (
                      <span
                        className="gr-absolute gr-rounded-full"
                        style={{
                          backgroundColor: '#22C55E',
                          width: '9px',
                          height: '9px',
                          top: '-1px',
                          right: '-1px',
                          border: '2px solid',
                          borderColor: isActive
                            ? 'var(--gordian-accent-color, #111827)'
                            : '#ffffff',
                        }}
                      />
                    )}
                  </div>
                  <div
                    className="gr-flex gr-flex-col gr-items-start"
                    style={{ gap: '1px' }}
                  >
                    <span
                      className="gr-text-sm gr-font-medium gr-whitespace-nowrap"
                      style={{
                        color: isActive
                          ? 'var(--gordian-accent-contrast-color, #ffffff)'
                          : 'var(--gordian-accent-color, #111827)',
                      }}
                    >
                      {pax.first_names} {pax.surname}
                    </span>
                    <span
                      className="gr-text-xs gr-whitespace-nowrap"
                      style={{
                        color: isActive ? 'rgba(255,255,255,0.55)' : '#9CA3AF',
                        fontWeight: paxSeat ? 600 : 400,
                      }}
                    >
                      {paxSeat ? paxSeat.seat : 'No seat'}
                    </span>
                  </div>
                  {isActive && (
                    <svg
                      className="gr-ml-auto gr-flex-shrink-0"
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                    >
                      <path
                        d="M2.5 7.5l3.5 3.5 6.5-6.5"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Fallback to native select when passenger data not available
  const selectId = `${id || 'passenger'}-select`;
  const labelId = `${selectId}-label`;
  const ariaLabel = 'Select passenger';

  return (
    <div
      className="gordian-select gr-flex-col gr-text-title gr-bg-white gr-w-3/6 gr-h-full gr-relative gr-border gr-text-sm"
      id={id}
    >
      <label id={labelId} htmlFor={selectId} className="gr-sr-only">
        {ariaLabel}
      </label>
      <select
        onChange={(event) => handleChange(event.target.value)}
        className="gr-block gr-appearance-none gr-w-full gr-h-full gr-bg-white gr-font-normal gr-rounded-none gr-pl-6 gr-pb-4"
        value={value}
        id={selectId}
        aria-label={ariaLabel}
        aria-labelledby={labelId}
        title={ariaLabel}
      >
        {children}
      </select>
      <div className="gr-pointer-events-none gr-absolute gr-inset-y-0 gr-right-0 gr-flex gr-items-center gr-px-2">
        <div className="gr-hidden sm:gr-block gr-text-xs gr-text-secondary-text gr-mr-1 sm:gr-mr-2">
          {outOfText}
        </div>
        <SelectArrow />
      </div>
    </div>
  );
}
